import * as React from 'react';
import { ReactElement, useCallback, useContext, useEffect, useState } from 'react';
import { AdminDataPersonDTO, ApiResponse, PatchDocument, PersonApi, PersonDTO } from '../../../api/core';
import { getAuthMiddleware } from '../../../Auth/utils/GetAuthMiddleware';
import { AuthContext } from '../../../Auth/components/AuthProvider';
import FormPerson from './FormPerson';
import { Blocker, NavigateFunction, Params, useBlocker, useLocation, useNavigate, useParams } from 'react-router';
import compare, { Patch } from '@mms-v3/patch-lib';
import { getDefaultPerson } from '../../../Settings/emptyModels';
import { ToastContext, ToastContextType } from '../../../util/providers/ToastProvider';
import { Loading } from '../../system/Loading';
import { IntlShape, useIntl } from 'react-intl';

export function EditPersonPage(): ReactElement {
  const navigate: NavigateFunction = useNavigate();
  const params: Readonly<Params<string>> = useParams();
  const {actions} = useContext(AuthContext);
  const location = useLocation();
  const personApi: PersonApi = new PersonApi().withMiddleware(getAuthMiddleware(actions));

  const [blockRequested, setBlockRequested] = React.useState<boolean>(false);
  const blocker: Blocker = useBlocker(useCallback((): boolean => blockRequested, [blockRequested]));
  const toast: ToastContextType = useContext(ToastContext);
  const intl: IntlShape = useIntl();

  const [person, setPerson] = useState<PersonDTO>();
  const [modifiedModel, setModifiedModel] = useState<PersonDTO>();
  const [saving, setSaving] = useState<boolean>(false);

  const updateField = (field: keyof PersonDTO, value: any): void => {
    setBlockRequested(true);
    setModifiedModel((state: PersonDTO): PersonDTO => ({...state, [field]: value}));
  };

  const updateAdminField = (field: keyof AdminDataPersonDTO, value: any): void => {
    setBlockRequested(true);
    setModifiedModel((state: PersonDTO): PersonDTO => ({...state, adminDataPerson: {...state.adminDataPerson, [field]: value}}));
  };

  useEffect((): void => {
    if (blocker.state === 'blocked') {
      if (!blockRequested) return blocker.proceed();
      toast.confirm({
        header: intl.formatMessage({id: 'popup.unsavedInput.pageChange.headline'}),
        message: intl.formatMessage({id: 'popup.unsavedInput.pageChange.message'}),
        icon: 'pi pi-info-circle',
        defaultFocus: 'reject',
        acceptClassName: 'p-button-danger',
        acceptLabel: intl.formatMessage({id: 'button.confirm'}),
        accept: (): void => {
          setModifiedModel(structuredClone(person));
          blocker.proceed();
        },
        rejectLabel: intl.formatMessage({id: 'button.abort'}),
        reject: (): void => blocker.reset(),
      });
    }
  }, [blocker.state]);

  useEffect((): void => setModifiedModel(structuredClone(person)), [person]);

  useEffect((): void => {
    if (params.id !== undefined) {
      const id: number = Number(params.id);
      if (isNaN(id)) {
        void navigate('/error/404');
        return;
      }
      void fetchData(id);
    } else {
      setPerson(getDefaultPerson());
    }
  }, [location.pathname]);

  const submit = async (): Promise<void> => {
    setSaving(true);
    try {
      let response: ApiResponse<PersonDTO>;
      if (!modifiedModel.id) {
        response = await personApi.createPersonRaw({personDTO: modifiedModel});
      } else {
        const patch: Patch[] = compare(person, modifiedModel);
        response = await personApi.updatePersonRaw({
          personId: modifiedModel.id,
          patchDocument: patch as PatchDocument[],
        });
      }
      if (response.raw.ok) {
        toast.notify({severity: 'success', summary: intl.formatMessage({id: 'message.saving.success.message'})});
        const value: PersonDTO = await response.value();
        setBlockRequested(false);
        void navigate(`/person/${value.id}`);
        return;
      }
      toast.error(response, 'message.saving.failed');
    } catch (response) {
      toast.error(response.response, 'message.saving.failed');
    }
    setSaving(false);
  };

  const fetchData = async (id: number): Promise<void> => {
    const response: ApiResponse<PersonDTO> = await personApi.getPersonRaw({personId: id});
    if (!response.raw.ok) {
      toast.notify({
        severity: 'error',
        summary: intl.formatMessage({id: 'message.loading.failed.title'}),
        detail: intl.formatMessage({id: 'message.loading.failed.message'}),
      });
      return navigate(`/error/${response.raw.status}`);
    }
    setPerson(await response.value());
  };
  if (!modifiedModel) {
    return React.createElement(Loading, {});
  }
  return React.createElement(FormPerson, { modifiedModel, submit, updateField, updateAdminField, saving });
}

export default EditPersonPage;
